import React, { useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Link, useHistory } from 'react-router-dom';
import { SectionProps } from '../utils/SectionProps';
import SectionHeader from '../components/sections/partials/SectionHeader';
import Button from '../components/elements/Button';
import Input from '../components/elements/Input';
import FormHint from '../components/elements/FormHint';
import { AuthContext } from '../context/AuthContext';
import { apiRequest } from '../api/client';

const propTypes = {
  ...SectionProps.types
};

const defaultProps = {
  ...SectionProps.defaults
};

const decodeBase64Url = (value) => {
  const normalizedValue = value.replace(/-/g, '+').replace(/_/g, '/');
  const paddedValue = normalizedValue.padEnd(Math.ceil(normalizedValue.length / 4) * 4, '=');

  try {
    return window.atob(paddedValue);
  } catch (error) {
    return '';
  }
};

const parseJwtPayload = (token) => {
  if (!token) {
    return null;
  }

  const tokenParts = token.split('.');

  if (tokenParts.length < 2) {
    return null;
  }

  try {
    return JSON.parse(decodeBase64Url(tokenParts[1]));
  } catch (error) {
    return null;
  }
};

const toDisplayValue = (value, fallback = 'Unavailable') => {
  if (value === null || value === undefined) {
    return fallback;
  }

  const trimmedValue = String(value).trim();
  return trimmedValue ? trimmedValue : fallback;
};

const formatRenewalDate = (value) => {
  if (!value) {
    return 'Renewal date unavailable.';
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return `Renews on ${value}`;
  }

  const day = parsedDate.getDate();
  const month = parsedDate.toLocaleString('en-GB', { month: 'long' });
  const year = parsedDate.getFullYear();
  const daySuffix = (() => {
    if (day >= 11 && day <= 13) {
      return 'th';
    }

    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  })();

  return `Renews on ${day}${daySuffix} ${month} ${year}`;
};

const resolveConnectedStoreUrl = (accountDetails) => (
  accountDetails.wordpress_site_url
  || accountDetails.site_url
  || accountDetails.connected_site_url
  || accountDetails.store_url
  || accountDetails.website_url
  || ''
);

const Account = ({
  className,
  topOuterDivider,
  bottomOuterDivider,
  topDivider,
  bottomDivider,
  hasBgColor,
  invertColor
}) => {
  const history = useHistory();
  const { auth, token, user, logout } = useContext(AuthContext);
  const [accountDetails, setAccountDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [billingError, setBillingError] = useState('');
  const [isBillingLoading, setIsBillingLoading] = useState(false);

  const jwtPayload = useMemo(() => parseJwtPayload(token), [token]);

  const userId = useMemo(() => {
    if (user && user.id) {
      return String(user.id);
    }

    if (user && user.user_id) {
      return String(user.user_id);
    }

    if (auth && auth.user && auth.user.id) {
      return String(auth.user.id);
    }

    if (auth && auth.user && auth.user.user_id) {
      return String(auth.user.user_id);
    }

    if (auth && auth.id) {
      return String(auth.id);
    }

    if (auth && auth.user_id) {
      return String(auth.user_id);
    }

    if (jwtPayload && jwtPayload.user_id) {
      return String(jwtPayload.user_id);
    }

    if (jwtPayload && jwtPayload.id) {
      return String(jwtPayload.id);
    }

    if (jwtPayload && jwtPayload.sub) {
      return String(jwtPayload.sub);
    }

    return '';
  }, [auth, jwtPayload, user]);

  const normalizedUserId = useMemo(() => (
    /^\d+$/.test(userId) ? Number(userId) : userId
  ), [userId]);

  useEffect(() => {
    let isMounted = true;

    const loadAccountDetails = async () => {
      if (!token || !userId) {
        if (isMounted) {
          setLoadError('We could not load your account details right now.');
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);
      setLoadError('');

      try {
        const response = await apiRequest('/api/v1/user_details', {
          method: 'POST',
          mode: 'cors',
          token,
          body: {
            user_id: normalizedUserId
          }
        });

        if (isMounted) {
          setAccountDetails(response);
        }
      } catch (error) {
        if (isMounted) {
          setLoadError(error.message || 'Unable to load your account details right now.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadAccountDetails();

    return () => {
      isMounted = false;
    };
  }, [normalizedUserId, token, userId]);

  const outerClasses = classNames(
    'signin section',
    topOuterDivider && 'has-top-divider',
    bottomOuterDivider && 'has-bottom-divider',
    hasBgColor && 'has-bg-color',
    invertColor && 'invert-color',
    className
  );

  const innerClasses = classNames(
    'signin-inner section-inner',
    topDivider && 'has-top-divider',
    bottomDivider && 'has-bottom-divider'
  );

  const sectionHeader = {
    title: 'Your Exanta account',
    paragraph: 'You are signed in and ready to access your protected resources.'
  };

  const handleLogout = () => {
    logout();
    history.push('/');
  };

  const handleUpgradeAccount = async () => {
    setBillingError('');
    setIsBillingLoading(true);

    try {
      const response = await apiRequest('/api/v1/create_checkout_session', {
        method: 'POST',
        mode: 'cors',
        token
      });

      const redirectUrl = response && (
        response.checkout_url
        || response.url
        || response.redirect_url
      );

      if (!redirectUrl) {
        throw new Error('Checkout URL unavailable.');
      }

      window.location.assign(redirectUrl);
    } catch (error) {
      setBillingError(error.message || 'Unable to open billing right now.');
    } finally {
      setIsBillingLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setBillingError('');
    setIsBillingLoading(true);

    try {
      const response = await apiRequest('/api/v1/create_billing_portal_session', {
        method: 'POST',
        mode: 'cors',
        token
      });

      if (!response || !response.portal_url) {
        throw new Error('Billing portal URL unavailable.');
      }

      window.location.href = response.portal_url;
    } catch (error) {
      setBillingError('Could not open billing portal. Please try again.');
    } finally {
      setIsBillingLoading(false);
    }
  };

  const rawTierName = accountDetails && (
    accountDetails.subscription_tier
    || accountDetails.tier_name
  );
  const tierName = toDisplayValue(rawTierName);
  const normalizedTierName = rawTierName ? String(rawTierName).trim().toLowerCase() : '';
  const isProTier = normalizedTierName === 'pro';
  const renewalDate = formatRenewalDate(
    accountDetails && (
      accountDetails.subscription_expiry_date
      || accountDetails.subscriptionExpiryDate
      || accountDetails.renewal_date
      || accountDetails.renewal_at
      || accountDetails.current_period_end
      || accountDetails.subscription_renewal_date
    )
  );
  const monthlyLimit = Number(accountDetails && accountDetails.monthly_limit) || 0;
  const currentMonthQueries = Number(accountDetails && accountDetails.current_month_queries) || 0;
  const usagePercent = monthlyLimit > 0
    ? Math.min(100, Math.round((currentMonthQueries / monthlyLimit) * 100))
    : 0;
  const connectedStoreUrl = toDisplayValue(resolveConnectedStoreUrl(accountDetails || {}), 'No connected store found.');
  const fullName = toDisplayValue([
    accountDetails && accountDetails.first_name,
    accountDetails && accountDetails.last_name
  ].filter(Boolean).join(' '));
  const email = toDisplayValue(accountDetails && accountDetails.email);
  const showManageSubscription = Boolean(accountDetails) && isProTier;

  return (
    <section className={outerClasses}>
      <div className="container">
        <div className={innerClasses}>
          <SectionHeader tag="h1" data={sectionHeader} className="center-content" />
          <div className="tiles-wrap">
            <div className="tiles-item account-tiles-item">
              <div className="tiles-item-inner has-shadow account-tile">
                {isLoading && (
                  <p className="account-status-message text-sm text-color-low mb-24">
                    Loading your account details...
                  </p>
                )}

                {loadError && (
                  <FormHint className="mb-24" status="error">{loadError}</FormHint>
                )}

                {!isLoading && !loadError && accountDetails && (
                  <React.Fragment>
                    <div className="account-section">
                      <h4 className="mt-0 mb-12">Current plan</h4>
                      <p className="account-plan-name h5 mt-0 mb-12">{tierName}</p>
                      {showManageSubscription ? (
                        <React.Fragment>
                          <Button
                            color="dark"
                            type="button"
                            wideMobile
                            loading={isBillingLoading}
                            disabled={isBillingLoading}
                            onClick={handleManageSubscription}
                          >
                            {isBillingLoading ? 'Loading...' : 'Manage Subscription'}
                          </Button>
                          <p className="account-inline-copy text-sm text-color-low mt-12 mb-0">
                            {renewalDate}
                          </p>
                        </React.Fragment>
                      ) : (
                        <Button
                          color="primary"
                          type="button"
                          wideMobile
                          loading={isBillingLoading}
                          disabled={isBillingLoading}
                          onClick={handleUpgradeAccount}
                        >
                          {isBillingLoading ? 'Loading...' : 'Upgrade to Pro'}
                        </Button>
                      )}
                      {billingError && (
                        <FormHint className="mt-12" status="error">{billingError}</FormHint>
                      )}
                    </div>

                    <div className="account-section">
                      <h4 className="mt-0 mb-12">Usage this month</h4>
                      <p className="account-inline-copy text-sm text-color-low mt-0 mb-16">
                        {monthlyLimit > 0
                          ? `${currentMonthQueries} of ${monthlyLimit} queries used this month`
                          : `${currentMonthQueries} queries used this month`}
                      </p>
                      <div className="account-usage-bar" aria-hidden="true">
                        <div
                          className="account-usage-bar-fill"
                          style={{ width: `${usagePercent}%` }}
                        />
                      </div>
                    </div>

                    <div className="account-section">
                      <h4 className="mt-0 mb-16">Connected store</h4>
                      <Input
                        id="account-connected-store"
                        type="text"
                        label="WordPress site URL"
                        value={connectedStoreUrl}
                        readOnly
                      />
                    </div>

                    <div className="account-section">
                      <h4 className="mt-0 mb-16">Account details</h4>
                      <div className="mb-16">
                        <Input
                          id="account-name"
                          type="text"
                          label="Name"
                          value={fullName}
                          readOnly
                        />
                      </div>
                      <div className="mb-16">
                        <Input
                          id="account-email"
                          type="email"
                          label="Email"
                          value={email}
                          readOnly
                        />
                      </div>
                      <Link to="/forgotpassword" className="func-link">Change password</Link>
                    </div>

                    <div className="account-section">
                      <h4 className="mt-0 mb-16">Actions</h4>
                      <div className="mb-16">
                        <Button tag={Link} to="/feedback" color="primary" wide>
                          Fill Out Feedback Form
                        </Button>
                      </div>
                    </div>
                  </React.Fragment>
                )}

                <div className="mb-16">
                  <Button color="dark" wide onClick={handleLogout}>
                    Logout
                  </Button>
                </div>
                <Button tag={Link} to="/download" color="primary" wide>
                  Go to Download
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

Account.propTypes = propTypes;
Account.defaultProps = defaultProps;

export default Account;
